import classNames from "classnames";
import {
	useForceRerender,
	usePreviousValue,
	useStableImperativeCallback,
	useSynchronizedRef
} from "client/Hooks";
import {
	EMPTY_ARRAY,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { EditorState, Transaction } from "prosemirror-state";
import { DirectEditorProps, EditorView } from "prosemirror-view";
import {
	forwardRef,
	MouseEvent,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useContext,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import {
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames
} from "../ControlAttachment";
import styles from "./ProsemirrorView.module.less";
import { flushSync } from "react-dom";

export type ProsemirrorViewProps = {
	onBlur?: (() => void);
	onFocus?: (() => void);
	placeholder?: string | ReactElement;
	shouldRenderPlaceholder: boolean;
	isInvalid?: boolean;
	isDisabled?: boolean;
	isBorderless?: boolean;
	hasNoPadding?: boolean;
	hasNoBackgroundColor?: boolean;
	height?: "auto" | "fill" | "resizable" | number;
	maxHeight?: number;
	attachedOn?: ControlAttachmentOptions;
	additionalClassName?: string;
	trackingId: string;
	editorState: EditorState;
	onChange: (newState: EditorState, isSignificant: boolean) => void;
	isSignificantChange?: (tr: Transaction) => boolean;
	id?: string;
} & Partial<DirectEditorProps>;

export type ProsemirrorViewHandle = {
	focus(): void;
	hasFocus(): boolean;
	getProsemirrorViewObject(): EditorView | null;
};

export const ProsemirrorView = forwardRef(
	(
		props: ProsemirrorViewProps,
		ref: Ref<ProsemirrorViewHandle>
	): ReactElement => {
		const {
			trackingId,
			attachedOn = EMPTY_ARRAY,
			additionalClassName: className,
			hasNoBackgroundColor = false,
			hasNoPadding = false,
			height,
			maxHeight,
			isBorderless = false,
			isDisabled = false,
			isInvalid = false,
			onBlur,
			onFocus,
			placeholder,
			shouldRenderPlaceholder,
			editorState,
			onChange,
			isSignificantChange,
			id,
			...viewProps
		} = props;

		const {
			attachedOn: attachmentsFromContext
		} = useContext(ControlAttachmentContext);

		const hostRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		const [rerender] = useForceRerender();
		const previousEditorState: EditorState | undefined = usePreviousValue(editorState);
		const previousViewProps: Partial<DirectEditorProps> | undefined = usePreviousValue(viewProps);

		const viewRef: RefObject<EditorView | null> = useRef<EditorView>(null);

		// NOTE: Ref values are needed in the effect that creates view.
		// First its being used for initialization of the view,
		// it is fine to not re-initialize the view with the new state,
		// because state of the view is being updated outside of the effect which creates view.
		//
		// Next its being used in the dispatch changes from view callback.
		// There we want to use the latest value of the editorState when someone changes the view.
		// Ref helps us with retrieving it without updating callbacks with the new closure
		const editorStateRef: ReadonlyRefObject<EditorState> = useSynchronizedRef(editorState);
		const stableOnChange: typeof onChange = useStableImperativeCallback(onChange);
		const [initialViewProps] = useState<Partial<DirectEditorProps>>(viewProps);

		const isDisabledRef: ReadonlyRefObject<boolean> = useSynchronizedRef(isDisabled);
		const isSignificantChangeRef: ReadonlyRefObject<((tr: Transaction) => boolean) | undefined> = useSynchronizedRef(isSignificantChange);

		useLayoutEffect(
			(): EffectWithDestructorResult => {
				if (!hostRef.current) {
					console.error("Host ref is not set");
					return;
				}

				const view = new EditorView(
					hostRef.current,
					{
						...initialViewProps,
						state: editorStateRef.current,
						dispatchTransaction: (tr) => {
							const editorState: EditorState = editorStateRef.current;
							const isSignificantChange: boolean = isSignificantChangeRef.current
								?.(tr) ?? false;

							// NOTE: We use flushSync to avoid state updates batching. Some plugins (columnResizing)
							// assume that the state on the view will be updated immediately as soon as
							// dispatchTransaction function is called. Due to the state batching
							// the second (and the following) transaction is called on the stale state.
							flushSync(
								(): void => {
									stableOnChange(
										editorState.apply(tr),
										isSignificantChange
									);
								}
							);
						},
						editable: () => !isDisabledRef.current
					}
				);

				viewRef.current = view;
				rerender();

				return (): void => {
					view.destroy();
				};
			},
			// NOTE: this deps array must contain only stable objects
			// otherwise view might get destroyed prematurely
			[
				editorStateRef,
				hostRef,
				stableOnChange,
				rerender,
				initialViewProps,
				isDisabledRef,
				isSignificantChangeRef
			]
		);

		useEffect(
			(): void => {
				// Since editable prop is calculated based on state, and isDisabled flag is not part of the state
				// Trigger state change so view could update its disabled status based on it
				hasImplicitDependencyOn(isDisabled);
				stableOnChange(
					editorStateRef.current
						.apply(editorStateRef.current.tr),
					false
				);
			},
			[
				editorStateRef,
				isDisabled,
				stableOnChange
			]
		);

		if (isNotNil(viewRef.current)) {
			if (previousEditorState !== editorState) {
				viewRef.current
					.updateState(editorState);
			}

			if (!shallowComparison(previousViewProps, viewProps)) {
				viewRef.current
					.setProps(viewProps);
			}
		}

		useImperativeHandle(
			ref,
			(): ProsemirrorViewHandle => ({
				focus(): void {
					viewRef.current
						?.focus();
				},
				hasFocus(): boolean {
					return viewRef.current
						?.hasFocus()
							?? false;
				},
				getProsemirrorViewObject(): EditorView | null {
					return viewRef.current;
				},
			})
		);

		const inputClickHandler = (e: MouseEvent): void => {
			e.preventDefault();
			viewRef.current
				?.focus();
		};

		return (
			<>
				<div
					{...setTrackingId(trackingId)}
					ref={hostRef}
					className={classNames(
						className,
						styles.root,
						{
							[styles.root_resizable]: height === "resizable",
							[styles.root_autoHeight]: height === "auto",
							[styles.root_fillHeight]: height === "fill",
							[styles.root_invalid]: isInvalid,
							[styles.root_disabled]: isDisabled,
							[styles.root_borderless]: isBorderless,
							[styles.root_noPadding]: hasNoPadding,
							[styles.root_hasNoBackgroundColor]: hasNoBackgroundColor
						},
						getAttachmentClassNames(attachedOn, attachmentsFromContext)
					)}
					style={{
						maxHeight,
						height: typeof height === "number"
							? height
							: undefined,
					}}
					onBlur={onBlur}
					onFocus={onFocus}
				>
					{shouldRenderPlaceholder
						&& (
							isNotNil(placeholder)
								|| (typeof placeholder === "string" && isNotNilOrEmpty(placeholder))
						) && (
							<span className={styles.placeholder}>
								{placeholder}
							</span>
						)
					}
				</div>
				{isNotNilOrEmpty(id) && (
					<input
						id={id}
						type="button"
						onClick={inputClickHandler}
						className={styles.input}
						disabled={isDisabled}
					/>
				)}
			</>
		);
	}
);
ProsemirrorView.displayName = "ProsemirrorView";